import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Star, 
  Calendar, 
  Save,
  X,
  Check,
  AlertCircle,
  FileText,
  BookOpen,
  BarChart3,
  Search,
  Filter,
  Tag,
  Image,
  Clock,
  User,
  TrendingUp
} from 'lucide-react';
import ImageUpload from '../ui/ImageUpload';
import { Hashtags, HashtagInput, HashtagFilter } from '../ui/Hashtags';
import { blogs, blogUtils, blogCategories } from '../../data/blogs';
import { 
  getBlogsFromAPI, 
  addBlog, 
  updateBlog, 
  deleteBlog, 
  toggleBlogStatus, 
  toggleBlogFeatured, 
  getBlogStats,
  getLanguageStats,
  getAvailableLanguages,
  getBlogsByLanguage
} from '../../utils/blogStorageHybrid';

export default function BlogAdminPanel() {
  const [blogsList, setBlogsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featuredImage: '',
    author: 'Альфира Герман',
    category: 'hypnotherapy',
    tags: [],
    status: 'draft',
    featured: false,
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    language: 'ru'
  });

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  // Генерация slug из заголовка
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Фильтрация блогов
  const getFilteredBlogs = () => {
    let filtered = blogsList;
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(blog => blog.status === filterStatus);
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(blog => blog.category === filterCategory);
    }
    
    if (filterLanguage !== 'all') {
      filtered = filtered.filter(blog => blog.language === filterLanguage);
    }
    
    if (selectedTags.length > 0) {
      filtered = filtered.filter(blog => {
        if (!blog.tags) return false;
        const tagsArray = Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim());
        return tagsArray.some(tag => selectedTags.includes(tag));
      });
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(blog => {
        const tagsArray = blog.tags ? (Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim())) : [];
        return blog.title.toLowerCase().includes(query) ||
               blog.excerpt.toLowerCase().includes(query) ||
               tagsArray.some(tag => tag.toLowerCase().includes(query));
      });
    }
    
    return filtered;
  };

  const filteredBlogs = getFilteredBlogs();

  // Получить все уникальные теги из блогов
  const getAllTags = () => {
    const allTags = new Set();
    blogsList.forEach(blog => {
      if (blog.tags) {
        const tagsArray = Array.isArray(blog.tags) ? blog.tags : blog.tags.split(',').map(tag => tag.trim());
        tagsArray.forEach(tag => allTags.add(tag));
      }
    });
    return Array.from(allTags).sort();
  };

  // Обработчики для работы с тегами
  const handleTagSelect = (tag) => {
    setSelectedTags(prev => [...prev, tag]);
  };

  const handleTagDeselect = (tag) => {
    setSelectedTags(prev => prev.filter(t => t !== tag));
  };

  const clearTagFilters = () => {
    setSelectedTags([]);
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [blogs, statsData, langStats] = await Promise.all([
          getBlogsFromAPI(),
          getBlogStats(),
          getLanguageStats()
        ]);
        
        setBlogsList(blogs);
        setStats(statsData);
        setLanguageStats(langStats);
        setLoading(false);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        setBlogsList([]);
        setStats({ total: 0, published: 0, draft: 0, featured: 0 });
        setLanguageStats({});
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Статистика (будет загружена асинхронно)
  const [stats, setStats] = useState({ total: 0, published: 0, draft: 0, featured: 0 });
  const [languageStats, setLanguageStats] = useState({});

  // Обработчики форм
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        title: value,
        slug: generateSlug(value)
      }));
    } else if (name === 'tags') {
      const tagsArray = value.split(',').map(tag => tag.trim()).filter(tag => tag);
      setFormData(prev => ({ ...prev, tags: tagsArray }));
    } else if (name === 'seoKeywords') {
      const keywordsArray = value.split(',').map(keyword => keyword.trim()).filter(keyword => keyword);
      setFormData(prev => ({ ...prev, seoKeywords: keywordsArray }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingBlog) {
        await updateBlog(editingBlog.id, formData);
      } else {
        await addBlog(formData);
      }
      
      // Обновляем список блогов
      const updatedBlogs = await getBlogsFromAPI();
      setBlogsList(updatedBlogs);
      
      // Обновляем статистику
      const [statsData, langStats] = await Promise.all([
        getBlogStats(),
        getLanguageStats()
      ]);
      setStats(statsData);
      setLanguageStats(langStats);
      
      resetForm();
      setShowCreateForm(false);
    } catch (error) {
      console.error('Ошибка при сохранении блога:', error);
      alert('Ошибка при сохранении блога');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featuredImage: '',
      author: 'Альфира Герман',
      category: 'hypnotherapy',
      tags: [],
      status: 'draft',
      featured: false,
      seoTitle: '',
      seoDescription: '',
      seoKeywords: []
    });
    setEditingBlog(null);
    setShowCreateForm(false);
  };

  const handleEdit = (blog) => {
    setFormData({
      ...blog,
      tags: Array.isArray(blog.tags) ? blog.tags : (blog.tags ? blog.tags.split(',').map(tag => tag.trim()) : []),
      seoKeywords: Array.isArray(blog.seoKeywords) ? blog.seoKeywords : (blog.seoKeywords ? blog.seoKeywords.split(',').map(keyword => keyword.trim()) : [])
    });
    setEditingBlog(blog);
    setShowCreateForm(true);
  };

  const handleDelete = async (blogId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот блог?')) {
      try {
        await deleteBlog(blogId);
        const updatedBlogs = await getBlogsFromAPI();
        setBlogsList(updatedBlogs);
        
        // Обновляем статистику
        const [statsData, langStats] = await Promise.all([
          getBlogStats(),
          getLanguageStats()
        ]);
        setStats(statsData);
        setLanguageStats(langStats);
      } catch (error) {
        console.error('Ошибка при удалении блога:', error);
        alert('Ошибка при удалении блога');
      }
    }
  };

  const toggleStatus = async (blogId) => {
    try {
      await toggleBlogStatus(blogId);
      const updatedBlogs = await getBlogsFromAPI();
      setBlogsList(updatedBlogs);
      
      // Обновляем статистику
      const [statsData, langStats] = await Promise.all([
        getBlogStats(),
        getLanguageStats()
      ]);
      setStats(statsData);
      setLanguageStats(langStats);
    } catch (error) {
      console.error('Ошибка при изменении статуса:', error);
      alert('Ошибка при изменении статуса');
    }
  };

  const toggleFeatured = async (blogId) => {
    try {
      await toggleBlogFeatured(blogId);
      const updatedBlogs = await getBlogsFromAPI();
      setBlogsList(updatedBlogs);
      
      // Обновляем статистику
      const [statsData, langStats] = await Promise.all([
        getBlogStats(),
        getLanguageStats()
      ]);
      setStats(statsData);
      setLanguageStats(langStats);
    } catch (error) {
      console.error('Ошибка при изменении рекомендуемого статуса:', error);
      alert('Ошибка при изменении рекомендуемого статуса');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleExport = () => {
    try {
      exportBlogs();
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      alert('Ошибка при экспорте блогов');
    }
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      importBlogs(file)
        .then((importedBlogs) => {
          setBlogsList(importedBlogs);
          alert('Блоги успешно импортированы');
        })
        .catch((error) => {
          console.error('Ошибка при импорте:', error);
          alert('Ошибка при импорте блогов: ' + error.message);
        });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Заголовок */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Управление блогами</h1>
            </div>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={handleExport}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Экспорт
              </button>
              
              <label className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 cursor-pointer">
                <FileText className="w-4 h-4" />
                Импорт
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </label>
              
              <button 
                onClick={() => setShowCreateForm(true)}
                className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Создать блог
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Загрузка блогов...</p>
            </div>
          </div>
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
          >
            {/* Статистика */}
            <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Всего</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Eye className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Опубликовано</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.published}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Edit className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Черновики</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.draft}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Star className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Рекомендуемые</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.featured}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Просмотры</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalViews}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <User className="w-6 h-6 text-pink-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Лайки</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.totalLikes}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Статистика по языкам */}
            <motion.div variants={item} className="bg-white rounded-lg p-6 shadow-sm mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Статистика по языкам</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(languageStats).map(([language, langStats]) => (
                  <div key={language} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {language === 'ru' ? '🇷🇺 Русский' : '🇩🇪 Deutsch'}
                      </h4>
                      <span className="text-sm text-gray-500">{langStats.total} блогов</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="text-center">
                        <div className="text-green-600 font-semibold">{langStats.published}</div>
                        <div className="text-gray-500">Опубликовано</div>
                      </div>
                      <div className="text-center">
                        <div className="text-yellow-600 font-semibold">{langStats.draft}</div>
                        <div className="text-gray-500">Черновики</div>
                      </div>
                      <div className="text-center">
                        <div className="text-blue-600 font-semibold">{langStats.featured}</div>
                        <div className="text-gray-500">Рекомендуемые</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Фильтры и поиск */}
            <motion.div variants={item} className="bg-white rounded-lg p-6 shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Поиск по блогам..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    <option value="all">Все статусы</option>
                    <option value="published">Опубликовано</option>
                    <option value="draft">Черновики</option>
                  </select>
                  
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    <option value="all">Все категории</option>
                    {blogCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <select
                    value={filterLanguage}
                    onChange={(e) => setFilterLanguage(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    <option value="all">Все языки</option>
                    <option value="ru">🇷🇺 Русский</option>
                    <option value="de">🇩🇪 Deutsch</option>
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Фильтр по тегам */}
            {getAllTags().length > 0 && (
              <motion.div variants={item} className="bg-white rounded-lg p-6 shadow-sm mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Фильтр по тегам</h3>
                  {selectedTags.length > 0 && (
                    <button
                      onClick={clearTagFilters}
                      className="text-sm text-rose-600 hover:text-rose-800 font-medium"
                    >
                      Очистить фильтры
                    </button>
                  )}
                </div>
                <HashtagFilter
                  tags={getAllTags()}
                  selectedTags={selectedTags}
                  onTagSelect={handleTagSelect}
                  onTagDeselect={handleTagDeselect}
                />
                {selectedTags.length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Активные фильтры:</strong> {selectedTags.join(', ')}
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Таблица блогов */}
            <motion.div variants={item} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input type="checkbox" className="rounded" />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Блог
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Язык
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Статус
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Категория
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Дата
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Просмотры
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Действия
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBlogs.map((blog) => (
                      <tr key={blog.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input type="checkbox" className="rounded" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              {blog.featuredImage ? (
                                <img 
                                  src={blog.featuredImage} 
                                  alt={blog.title}
                                  className="h-12 w-12 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                  <FileText className="w-6 h-6 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                {blog.title}
                                {blog.featured && (
                                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {blog.excerpt.substring(0, 60)}...
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <Clock className="w-3 h-3" />
                                  {blog.readingTime} мин
                                </div>
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <User className="w-3 h-3" />
                                  {blog.author}
                                </div>
                              </div>
                              {blog.tags && blog.tags.length > 0 && (
                                <div className="mt-2">
                                  <Hashtags 
                                    tags={blog.tags.slice(0, 3)} 
                                    className="text-xs"
                                  />
                                  {blog.tags.length > 3 && (
                                    <span className="text-xs text-gray-500 ml-2">
                                      +{blog.tags.length - 3} еще
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            blog.language === 'ru' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {blog.language === 'ru' ? '🇷🇺 RU' : '🇩🇪 DE'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            blog.status === 'published' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {blog.status === 'published' ? 'Опубликовано' : 'Черновик'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            blogCategories.find(cat => cat.id === blog.category)?.color || 'bg-gray-100 text-gray-800'
                          }`}>
                            {blogCategories.find(cat => cat.id === blog.category)?.name || blog.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(blog.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            {blog.views}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleEdit(blog)}
                              className="text-rose-600 hover:text-rose-900"
                              title="Редактировать"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => toggleStatus(blog.id)}
                              className={blog.status === 'published' ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                              title={blog.status === 'published' ? 'Снять с публикации' : 'Опубликовать'}
                            >
                              {blog.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button 
                              onClick={() => toggleFeatured(blog.id)}
                              className={blog.featured ? 'text-yellow-600 hover:text-yellow-900' : 'text-gray-600 hover:text-gray-900'}
                              title={blog.featured ? 'Убрать из рекомендуемых' : 'Добавить в рекомендуемые'}
                            >
                              <Star className={`w-4 h-4 ${blog.featured ? 'fill-current' : ''}`} />
                            </button>
                            <button 
                              onClick={() => handleDelete(blog.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Удалить"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Модальное окно создания/редактирования */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingBlog ? 'Редактировать блог' : 'Создать новый блог'}
              </h2>
              <button 
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Язык *
                  </label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    required
                  >
                    <option value="ru">🇷🇺 Русский</option>
                    <option value="de">🇩🇪 Deutsch</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Заголовок *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="Введите заголовок блога"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL slug
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                    placeholder="url-slug"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Краткое описание *
                </label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="Краткое описание блога"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Содержимое *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  placeholder="Содержимое блога (HTML)"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ImageUpload
                    value={formData.featuredImage}
                    onChange={(value) => setFormData(prev => ({ ...prev, featuredImage: value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Автор
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Категория
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    {blogCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Статус
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  >
                    <option value="draft">Черновик</option>
                    <option value="published">Опубликовано</option>
                  </select>
                </div>
              </div>

              <div>
                <HashtagInput
                  value={formData.tags}
                  onChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                  placeholder="гипноз, терапия, страх"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-rose-600 focus:ring-rose-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Рекомендуемый блог
                </label>
              </div>

              {/* SEO поля */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">SEO настройки</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO заголовок
                    </label>
                    <input
                      type="text"
                      name="seoTitle"
                      value={formData.seoTitle}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="SEO заголовок"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO описание
                    </label>
                    <textarea
                      name="seoDescription"
                      value={formData.seoDescription}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="SEO описание"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO ключевые слова (через запятую)
                    </label>
                    <input
                      type="text"
                      name="seoKeywords"
                      value={formData.seoKeywords}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                      placeholder="гипноз, терапия, лечение"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingBlog ? 'Сохранить изменения' : 'Создать блог'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}